'use client';

import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { PermissionIdsType } from '@/consts/permissions';
import { useAuth } from '../AuthProvider';
import { useUser } from '../UserProvider';

const EMPTY_SET = new Set<PermissionIdsType>();
interface PermissionsContextType {
  permissions: Set<PermissionIdsType>;
  isLoading: boolean;
  hasPermission: (path: PermissionIdsType) => boolean;
  hasAnyPermission: (paths: PermissionIdsType[]) => boolean;
  hasAllPermissions: (paths: PermissionIdsType[]) => boolean;
}

const PermissionsContext = createContext<PermissionsContextType>({
  permissions: EMPTY_SET,
  isLoading: false,
  hasPermission: () => false,
  hasAnyPermission: () => false,
  hasAllPermissions: () => false,
});

interface Props {
  children: React.ReactNode;
}

export const PermissionsProvider = ({ children }: Props) => {
  const { auth } = useAuth();
  const { user } = useUser();
  const isLoading = useMemo(() => !auth?.token || !user, [auth?.token, user]);

  const [permissions, setPermissions] = useState<Set<PermissionIdsType>>(EMPTY_SET);

  useEffect(() => {
    if (user?.permissions) {
      setPermissions(new Set(user.permissions as PermissionIdsType[]));
    } else {
      setPermissions(EMPTY_SET);
    }
  }, [user?.permissions]);

  const hasPermission = useCallback(
    (path: PermissionIdsType): boolean => {
      return permissions.has(path);
    },
    [permissions]
  );

  const hasAnyPermission = useCallback(
    (paths: PermissionIdsType[]): boolean => {
      for (const path of paths) {
        if (permissions.has(path)) return true;
      }
      return false;
    },
    [permissions]
  );

  const hasAllPermissions = useCallback(
    (paths: PermissionIdsType[]): boolean => {
      return paths.every((path) => permissions.has(path));
    },
    [permissions]
  );

  const value = useMemo(
    () => ({
      permissions,
      isLoading,
      hasPermission,
      hasAnyPermission,
      hasAllPermissions,
    }),
    [permissions, hasPermission, hasAnyPermission, hasAllPermissions, isLoading]
  );

  return <PermissionsContext.Provider value={value}>{children}</PermissionsContext.Provider>;
};

export const usePermissions = () => {
  return useContext(PermissionsContext);
};
